import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Drawer,
  IconButton,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import InboxIcon from "@mui/icons-material/Inbox";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { styled } from "@mui/material/styles";
import SideBar from "../Components/SideBar";
import { url } from "../Components/url";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListViewMode } from "../hooks/useListPreferences";
import { GlobalSearchBar } from "../Components/ERPList";
import styles from "./AllEnquiries.module.scss";

const LIST_ID = "assign-visa-counsellor";
const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": { transform: "translateY(-4px)", boxShadow: theme.shadows[10] },
}));

const theme = createTheme({
  palette: { primary: { main: "#5e72e4" }, background: { default: "#f8f9fe" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", h4: { fontWeight: 600 }, button: { textTransform: "none" } },
  shape: { borderRadius: 12 },
});

function VisaCounsellorAssignment() {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [enquiries, setEnquiries] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [selectedCounsellor, setSelectedCounsellor] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const toggleViewMode = () => setViewMode((prev) => (prev === "cards" ? "table" : "cards"));

  const fetchEnquiries = async () => {
    try {
      const response = await fetchWithAuth(`${url}/get_null_visa_counsellor`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch enquiries");
      const data = await response.json();
      setEnquiries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounsellors = async () => {
    try {
      const response = await fetchWithAuth(`${url}/get_visa_counsellors`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch counsellors");
      const data = await response.json();
      setCounsellors(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchEnquiries();
    fetchCounsellors();
  }, []);

  const searchLower = (debouncedGlobalSearch || "").trim().toLowerCase();
  const filteredEnquiries = searchLower
    ? enquiries.filter((e) => {
        const name = `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
        const id = String(e.id || "");
        const counsellor = (e.counsellor || "").toLowerCase();
        const status = (e.status || "").toLowerCase();
        return name.includes(searchLower) || id.includes(searchLower) || counsellor.includes(searchLower) || status.includes(searchLower);
      })
    : enquiries;

  const handleAssignClick = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setSelectedCounsellor("");
    setShowModal(true);
  };

  const handleAssignSubmit = async () => {
    if (!selectedEnquiry) return;
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${url}/update_visa_counsellor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enquiry_id: selectedEnquiry.id,
          visa_counsellor_id: selectedCounsellor,
        }),
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to assign counsellor");
      }
      setSuccess("Visa counsellor assigned successfully");
      setShowModal(false);
      fetchEnquiries();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        {isMobile && (
          <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
            <Box sx={{ width: 250, p: 2 }}>
              <IconButton onClick={() => setSidebarOpen(false)} sx={{ mb: 2 }}>
                <CloseIcon />
              </IconButton>
              <SideBar />
            </Box>
          </Drawer>
        )}
        <Grid container spacing={0}>
          {!isMobile && (
            <Grid item xs={12} md={2} className={`${styles.sidebarGrid} sidebar-column`}>
              <div className="sidebar-wrapper">
                <SideBar />
              </div>
            </Grid>
          )}
          <Grid item xs={12} md={isMobile ? 12 : 10} className={styles.mainContent}>
            <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  {isMobile && (
                    <IconButton onClick={() => setSidebarOpen(true)} edge="start">
                      <MenuIcon />
                    </IconButton>
                  )}
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Visa Counsellor Assignment
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Assign visa counsellors to enquiries.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search name, ID, counsellor, status…"
                />
                <Box className={styles.headerActions}>
                  <Tooltip title={viewMode === "cards" ? "Table View" : "Card View"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                  <Button variant="contained" onClick={fetchEnquiries}>
                    Refresh
                  </Button>
                </Box>
              </Box>

              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {loading && enquiries.length === 0 && (
                <Box className={styles.loadingContainer}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                </Box>
              )}

              {!loading && filteredEnquiries.length === 0 && (
                <Box className={styles.emptyState}>
                  <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No enquiries currently require visa counsellor assignment.
                  </Typography>
                </Box>
              )}

              {!loading && filteredEnquiries.length > 0 && (
                <>
                  {viewMode === "cards" ? (
                    <Grid container spacing={2} className={styles.cardsGrid}>
                      {filteredEnquiries.map((enquiry) => (
                        <Grid item xs={12} sm={6} md={4} key={enquiry.id}>
                          <StyledCard>
                            <CardHeader
                              avatar={
                                <Avatar sx={{ bgcolor: "primary.main" }}>
                                  {enquiry.first_name?.charAt(0) || "N"}
                                </Avatar>
                              }
                              title={`${enquiry.first_name || ""} ${enquiry.last_name || ""}`}
                              subheader={`ID: ${enquiry.id}`}
                              className={styles.cardHeader}
                            />
                            <CardContent className={styles.cardContent}>
                              <Typography variant="body2">
                                <strong>Counsellor:</strong> {enquiry.counsellor || "Not assigned"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Status:</strong> {enquiry.status || "—"}
                              </Typography>
                              <Button
                                variant="contained"
                                size="small"
                                fullWidth
                                sx={{ mt: 1 }}
                                onClick={() => handleAssignClick(enquiry)}
                              >
                                Assign Visa Counsellor
                              </Button>
                            </CardContent>
                          </StyledCard>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Paper variant="outlined" className={styles.tableContainer}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Enquiry ID</TableCell>
                            <TableCell>Client Name</TableCell>
                            <TableCell>Assigned Counsellor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredEnquiries.map((enquiry) => (
                            <TableRow key={enquiry.id} hover className={styles.tableRow}>
                              <TableCell>{enquiry.id}</TableCell>
                              <TableCell className={styles.primaryText}>
                                {enquiry.first_name} {enquiry.last_name}
                              </TableCell>
                              <TableCell className={styles.secondaryText}>
                                {enquiry.counsellor || "Not assigned"}
                              </TableCell>
                              <TableCell>{enquiry.status}</TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleAssignClick(enquiry)}
                                >
                                  Assign Visa Counsellor
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </>
              )}

              <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Visa Counsellor</DialogTitle>
                <DialogContent>
                  {selectedEnquiry && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Client:</strong> {selectedEnquiry.first_name} {selectedEnquiry.last_name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Current Counsellor:</strong> {selectedEnquiry.counsellor || "Not assigned"}
                      </Typography>
                    </Box>
                  )}
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel>Select Visa Counsellor</InputLabel>
                    <Select
                      value={selectedCounsellor}
                      label="Select Visa Counsellor"
                      onChange={(e) => setSelectedCounsellor(e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value="">Select a counsellor</MenuItem>
                      {counsellors.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.username} ({c.first_name} {c.last_name})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowModal(false)} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAssignSubmit}
                    disabled={!selectedCounsellor || loading}
                  >
                    {loading ? "Assigning…" : "Assign"}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
}

export default VisaCounsellorAssignment;
